import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, ViewEncapsulation } from '@angular/core';
import { App } from '@df/core/app';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { RatioOptions } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { GSAPModule } from '@df/ui/display/gsap/gsap.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { LocalVimeoPlayerComponent } from '../../../../../ui/display/media/vimeo-player.component';
import { BASIC_COMPONENT_FIELDS, CMS_COMPONENT_CWV_CONFIG, ICmsComponentCwvConfig } from '../../../../config/component.config';

export interface IPageComponentFeatureBannerData extends NICmsContentComponent.IData, ICmsComponentCwvConfig {
    section: ITwoImagesWithCTAs[];
}

export interface ITwoImagesWithCTAs {
    size: 'small' | 'medium' | 'full';
    layout: 'button' | 'quotation' | 'button_copy' | 'copy';
    ratio?: string;
    image?: string;
    video_id?: string;
    video_autoplay: boolean;
    video_background: boolean;
    button_color?: string;
    button_label?: string;
    link?: string;
    quote?: string;
    copy_first?: string;
    copy_second?: string;
    copy_third?: string;
    caption?: string;
    text_below: 'button' | 'quote';
}

const small = (data: ITwoImagesWithCTAs) => data.size === 'small';
const medium = (data: ITwoImagesWithCTAs) => data.size === 'medium';
const full = (data: ITwoImagesWithCTAs) => data.size === 'full';
const buttonCopy = (data: ITwoImagesWithCTAs) => data.layout === 'button_copy';
const quotation = (data: ITwoImagesWithCTAs) => data.layout === 'quotation';

@ICmsComponent<PageComponentFeatureBannerComponent>({
    id: 'feature-banner',
    name: 'Feature Banner 1',
    image: 'assets/images/cms/feature-banner.png',
    tags: ['Homepage', 'Banner', 'Video'],
    fields: [
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'section',
            title: 'sections',
            itemName: 'Section',
            maxSize: 2,
            defaultSize: 2,
            fields: [
                <NICmsContentField.IChoiceField>{
                    key: 'size',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Display size',
                    required: true,
                    default: 'medium',
                    options: [
                        { key: 'small', title: 'Small' },
                        { key: 'medium', title: 'Medium' },
                        { key: 'full', title: 'Full' }
                    ]
                },
                {
                    type: NICmsContentField.EFieldType.label,
                    key: 'general',
                    title: 'General'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'ratio',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Image or video ratio',
                    options: RatioOptions,
                    description: 'Proportions width x height',
                    default: 'ratio-2-1'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'link',
                    title: 'Link',
                    default: '/'
                },
                {
                    type: NICmsContentField.EFieldType.label,
                    key: 'image-video-section',
                    title: 'Image/video'
                },
                {
                    type: NICmsContentField.EFieldType.image,
                    key: 'image',
                    title: 'Image',
                    default: ''
                },
                {
                    type: NICmsContentField.EFieldType.label,
                    key: 'video-section',
                    title: 'Video'
                },
                {
                    key: 'video_id',
                    type: NICmsContentField.EFieldType.text,
                    title: 'ID or URL',
                    description: 'Video ID or URL, e.g. https://vimeo.com/442627127'
                },
                {
                    type: NICmsContentField.EFieldType.label,
                    key: 'bottom-section',
                    title: 'Bottom section',
                    canShow: small
                },
                <NICmsContentField.IChoiceField>{
                    key: 'layout',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Layout',
                    required: true,
                    default: 'button',
                    options: [
                        { key: 'button', title: 'Button' },
                        { key: 'quotation', title: 'Quote' },
                        { key: 'button_copy', title: 'Button + Copy' },
                        { key: 'copy', title: 'Copy' }
                    ],
                    canShow: small
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'quote',
                    title: 'Quote',
                    default: 'Lorem ipsum dolor sit amet',
                    canShow: data => quotation(data) && small(data)
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'caption',
                    title: 'Caption',
                    default: 'Rachel zoe',
                    canShow: data => quotation(data) && small(data)
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'copy_first',
                    title: 'Copy - first line',
                    canShow: data => (data.layout === 'copy' || buttonCopy(data)) && small(data)
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'copy_second',
                    title: 'Copy - second line',
                    canShow: data => (data.layout === 'copy' || buttonCopy(data)) && small(data)
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'copy_third',
                    title: 'Copy - third line',
                    canShow: data => data.layout === 'copy' && small(data)
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'button_label',
                    title: 'Button label',
                    default: 'Find out more',
                    canShow: data => (buttonCopy(data) || data.layout === 'button') && (small(data) || medium(data) || full(data))
                }
            ]
        },
        ...BASIC_COMPONENT_FIELDS,
        ...CMS_COMPONENT_CWV_CONFIG
    ]
})
@Component({
    selector: 'page-component-feature-banner',
    templateUrl: './feature-banner.html',
    styleUrl: './feature-banner.scss',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [CommonModule, DfImageDirective, HrefComponent, GSAPModule, ICmsClientModule, LocalVimeoPlayerComponent, ShellComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageComponentFeatureBannerComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: IPageComponentFeatureBannerData;

    protected app = inject(App);

    hasBeforeColumn(item: ITwoImagesWithCTAs, index: number): boolean {
        if (this.client.isS) {
            return false;
        }
        switch (item.size) {
            case 'full':
                return false;
            case 'medium':
                return !index;
            case 'small':
                return true;
        }
    }

    hasAfterColumn(item: ITwoImagesWithCTAs, index: number): boolean {
        if (this.client.isS) {
            return false;
        }

        switch (item.size) {
            case 'full':
            case 'medium':
                return false;
            case 'small':
                return !index;
        }
    }

    getColumnClass(item: ITwoImagesWithCTAs, index: number): string {
        if (this.client.isS) {
            return 'c-12';
        }

        switch (item.size) {
            case 'full':
                return 'c-6' + (index ? ' m-r-0' : '');
            case 'medium':
                return 'c-5 p-l p-r' + (index ? ' m-r-0' : '');
            case 'small':
                return 'c-4' + (index ? ' p-r-8' : ' p-l-8');
        }
    }

    getColumnInnerClass(item: ITwoImagesWithCTAs, index: number): string {
        if (this.client.isS) {
            return 'c-12';
        }
        switch (item.size) {
            case 'full':
                return index ? ' m-l--1' : ' m-r--1';
        }
    }

    canAnimate() {
        return !this.client.isCms && this.component?.animation && this.app.isPlatformBrowser;
    }
}
